import { forwardRef, type ReactNode, type Ref } from 'react';
import type { BaseProps } from '../../theme/index.js';
import { useBaseAttrs, splitBaseProps } from '../_internal/factory.js';
import './Badge.css';

export interface BadgeProps extends BaseProps {
  children?: ReactNode;
  leftSection?: ReactNode;
  rightSection?: ReactNode;
  /** filled (default) | light | outline | dot | gradient */
  variant?: 'filled' | 'light' | 'outline' | 'dot' | 'gradient';
  /** xs (default) | sm | md | lg | xl */
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  /** Pill (default) or rounded square. */
  circle?: boolean;
}

/** Compact status/category indicator. */
export const Badge = forwardRef(function Badge(
  { children, leftSection, rightSection, variant = 'filled', size = 'sm', circle, ...rest }: BadgeProps,
  ref: Ref<HTMLSpanElement>
): ReactNode {
  const base = useBaseAttrs('rr-badge', rest, [
    `rr-badge--${variant}`,
    `rr-badge--size-${size}`,
    circle && 'rr-badge--circle'
  ]);
  const { dom } = splitBaseProps(rest);
  return (
    <span {...dom} ref={ref} {...base.attrs} className={base.className} style={base.style}>
      {leftSection ? <span className="rr-badge__section rr-badge__section--left">{leftSection}</span> : null}
      <span className="rr-badge__label">{children}</span>
      {rightSection ? <span className="rr-badge__section rr-badge__section--right">{rightSection}</span> : null}
    </span>
  );
});
